const STORAGE_KEY = 'haist_backend';

export const BACKENDS = {
  prod: 'https://backend.gethaist.com',
  dev: 'https://dev.backend.gethaist.com',
  local: 'http://localhost:8000',
} as const;

export type BackendMode = keyof typeof BACKENDS;

export function getBackendMode(): BackendMode {
  const onLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
  const stored = localStorage.getItem(STORAGE_KEY);
  // A 'local' preference from a public-origin frontend (e.g. gethaist.com)
  // would point at http://localhost:8000, which Chrome blocks via Private
  // Network Access and CORS. Auto-clear the stale preference.
  if (stored === 'local' && !onLocalhost) {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  } else {
    if (stored === 'dev') return 'dev';
    if (stored === 'local') return 'local';
    if (stored === 'prod') return 'prod';
  }
  if (onLocalhost) return 'local';
  // dev.gethaist.com is built in production mode (Dockerfile → default nx
  // configuration), so the MODE check below sent it to the prod backend and
  // dev report links rendered the prod conversation with the same id.
  // Decide by host first.
  if (typeof window !== 'undefined' && window.location.hostname.startsWith('dev.')) return 'dev';
  // No stored preference on a deployed build: default to the backend that
  // matches the build. The dev frontend (built with mode=development) talks to
  // the dev backend; the prod build talks to prod. Users can still override via
  // the backend switcher (setBackendMode, persisted in localStorage).
  if (import.meta.env.MODE === 'development') return 'dev';
  return 'prod';
}

export function getBackendUrl(): string {
  return BACKENDS[getBackendMode()];
}

export function setBackendMode(mode: BackendMode): void {
  localStorage.setItem(STORAGE_KEY, mode);
  window.location.reload();
}

export function isDevMode(): boolean {
  return getBackendMode() === 'dev';
}

/**
 * Resolve a stored blob name (or already-absolute URL) to its public
 * storage.gethaist.com URL. Mirrors the backend's get_public_url so edited /
 * original photo variants (stored as bare blob names) can be displayed.
 */
export function publicUrl(blobOrUrl: string | undefined | null): string {
  if (!blobOrUrl) return '';
  return blobOrUrl.startsWith('http') ? blobOrUrl : `https://storage.gethaist.com/${blobOrUrl}`;
}

/**
 * Convert a storage.gethaist.com URL to a thumb.gethaist.com thumbnail URL.
 * Pass width/height for the desired size. Returns original URL if not a storage URL.
 */
export function thumb(url: string | undefined | null): string {
  if (!url) return '';
  return url.replace('https://storage.gethaist.com/', 'https://thumb.gethaist.com/') + '?width=240&height=180&mode=fit&format=jpeg';
}
